import DateInput from '../../components/DateInput';
/**
 * AdminTreatmentPackages — sold packages + clinic-defined package builder.
 * Iter 145: package templates replace hard-coded presets. Admin/reception
 * create templates (manual price, 1/2/4/6-month duration); the "Sell a
 * package" flow picks from them and auto-computes expiry from the duration.
 */
import React, { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { safeConfirm } from '../../lib/confirm';
import { Package, Plus, Edit2, Trash2, RotateCcw, Loader2, LayoutTemplate } from 'lucide-react';
import { toast } from 'sonner';

const DISCIPLINES = ['physiotherapy', 'osteopathy', 'chiropractic', 'massage_therapy', 'fitness'];
const DURATIONS = [1, 2, 4, 6];

export default function AdminTreatmentPackages() {
  const [tab, setTab] = useState('sold');
  const [packages, setPackages] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [patients, setPatients] = useState([]);
  const [showSell, setShowSell] = useState(false);
  const [editPkg, setEditPkg] = useState(null);
  const [showTemplate, setShowTemplate] = useState(false);
  const [editTemplate, setEditTemplate] = useState(null);
  const [busyId, setBusyId] = useState('');

  const load = async () => {
    const r = await api.get('/treatment-packages');
    setPackages(r.data || []);
  };
  const loadTemplates = async () => {
    const r = await api.get('/package-templates');
    setTemplates(r.data || []);
  };

  useEffect(() => {
    load();
    loadTemplates();
    api.get('/patients').then(r => setPatients(r.data || [])).catch(() => {});
  }, []);

  const redeem = async (id) => {
    setBusyId(id);
    try {
      const r = await api.post(`/treatment-packages/${id}/redeem`);
      setPackages(p => p.map(x => x.package_id === id ? r.data : x));
      toast.success('Redeemed one session.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed.');
    } finally { setBusyId(''); }
  };

  const restore = async (id) => {
    setBusyId(id);
    try {
      const r = await api.post(`/treatment-packages/${id}/restore`);
      setPackages(p => p.map(x => x.package_id === id ? r.data : x));
      toast.success('Restored one session.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed.');
    } finally { setBusyId(''); }
  };

  const del = async (id) => {
    if (!(await safeConfirm('Delete this package?'))) return;
    try {
      await api.delete(`/treatment-packages/${id}`);
      setPackages(p => p.filter(x => x.package_id !== id));
      toast.success('Deleted.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed.');
    }
  };

  const delTemplate = async (id) => {
    if (!(await safeConfirm('Delete this package template? Already-sold packages are not affected.'))) return;
    try {
      await api.delete(`/package-templates/${id}`);
      setTemplates(t => t.filter(x => x.template_id !== id));
      toast.success('Template deleted.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed.');
    }
  };

  return (
    <div className="p-4 md:p-8 max-w-6xl">
      <div className="flex items-end justify-between gap-3 mb-6 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-accent font-medium mb-2">REVENUE · RETENTION</div>
          <h1 className="text-3xl md:text-4xl font-heading font-bold tracking-tight">Treatment packages</h1>
          <p className="text-fg-muted mt-2 text-sm">Design your own package products, then sell them as session blocks. Each completed visit auto-counts down.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => { setEditTemplate(null); setShowTemplate(true); }} className="btn-secondary inline-flex items-center gap-2" data-testid="tp-new-template-btn">
            <LayoutTemplate className="w-4 h-4"/> Create a package
          </button>
          <button onClick={() => setShowSell(true)} className="btn-primary inline-flex items-center gap-2" data-testid="tp-create-btn">
            <Plus className="w-4 h-4"/> Sell a package
          </button>
        </div>
      </div>

      <div className="flex gap-2 mb-4">
        <button onClick={() => setTab('sold')} className={`px-4 py-2 text-sm rounded-md ${tab === 'sold' ? 'bg-brand text-white' : 'border border-soft'}`} data-testid="tp-tab-sold">Sold packages ({packages.length})</button>
        <button onClick={() => setTab('templates')} className={`px-4 py-2 text-sm rounded-md ${tab === 'templates' ? 'bg-brand text-white' : 'border border-soft'}`} data-testid="tp-tab-templates">Package builder ({templates.length})</button>
      </div>

      {tab === 'sold' && (
        <div className="bento-card overflow-x-auto" data-testid="tp-list">
          {packages.length === 0 ? (
            <div className="text-center py-8 text-sm text-fg-muted">No packages sold yet. Click "Sell a package" to sell the first one.</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-fg-muted text-xs uppercase tracking-wider border-b border-soft">
                  <th className="text-left py-2 px-2">Patient</th>
                  <th className="text-left py-2 px-2">Package</th>
                  <th className="text-left py-2 px-2">Discipline</th>
                  <th className="text-left py-2 px-2">Progress</th>
                  <th className="text-left py-2 px-2">Status</th>
                  <th className="text-right py-2 px-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {packages.map(p => {
                  const pct = p.sessions_total ? Math.round(p.sessions_used / p.sessions_total * 100) : 0;
                  const remaining = (p.sessions_total || 0) - (p.sessions_used || 0);
                  return (
                    <tr key={p.package_id} className="border-b border-soft/50 hover:bg-bg-secondary/30" data-testid={`tp-row-${p.package_id}`}>
                      <td className="py-3 px-2 font-medium">{p.patient_name || '—'}</td>
                      <td className="py-3 px-2">{p.name}<div className="text-[10px] text-fg-muted">{p.price ? `${p.price} ${p.currency}` : ''}{p.expires_on ? ` · expires ${p.expires_on}` : ''}</div></td>
                      <td className="py-3 px-2 capitalize text-xs text-fg-muted">{(p.discipline || '').replace('_', ' ')}</td>
                      <td className="py-3 px-2">
                        <div className="text-xs mb-0.5">{p.sessions_used} / {p.sessions_total} · {remaining} left</div>
                        <div className="w-32 h-1.5 bg-bg-secondary rounded-full overflow-hidden">
                          <div className="h-full bg-brand transition-all" style={{ width: `${pct}%` }}/>
                        </div>
                      </td>
                      <td className="py-3 px-2">
                        <span className={`badge-${p.status === 'completed' ? 'success' : p.status === 'cancelled' ? 'error' : 'neutral'}`}>{p.status}</span>
                      </td>
                      <td className="py-3 px-2 text-right space-x-1">
                        {p.status === 'active' && remaining > 0 && (
                          <button onClick={() => redeem(p.package_id)} disabled={busyId === p.package_id}
                                  className="text-xs text-brand hover:opacity-70 inline-flex items-center gap-1"
                                  title="Manually count down one session"
                                  data-testid={`tp-redeem-${p.package_id}`}>
                            {busyId === p.package_id ? <Loader2 className="w-3 h-3 animate-spin"/> : '−1'}
                          </button>
                        )}
                        {p.sessions_used > 0 && (
                          <button onClick={() => restore(p.package_id)} disabled={busyId === p.package_id}
                                  className="text-xs text-fg-muted hover:text-brand inline-flex items-center gap-1"
                                  title="Undo last redemption"
                                  data-testid={`tp-restore-${p.package_id}`}>
                            <RotateCcw className="w-3 h-3"/>
                          </button>
                        )}
                        <button onClick={() => setEditPkg(p)} className="text-xs text-fg-muted hover:text-brand" data-testid={`tp-edit-${p.package_id}`}>
                          <Edit2 className="w-3 h-3"/>
                        </button>
                        <button onClick={() => del(p.package_id)} className="text-xs text-[#B83A3A] hover:opacity-70" data-testid={`tp-delete-${p.package_id}`}>
                          <Trash2 className="w-3 h-3"/>
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {tab === 'templates' && (
        <div className="bento-card overflow-x-auto" data-testid="tp-template-list">
          {templates.length === 0 ? (
            <div className="text-center py-8 text-sm text-fg-muted">No package products yet. Click "Create a package" to define your first one (name, sessions, your price, duration).</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-fg-muted text-xs uppercase tracking-wider border-b border-soft">
                  <th className="text-left py-2 px-2">Name</th>
                  <th className="text-left py-2 px-2">Sessions</th>
                  <th className="text-left py-2 px-2">Price</th>
                  <th className="text-left py-2 px-2">Duration</th>
                  <th className="text-left py-2 px-2">Discipline</th>
                  <th className="text-right py-2 px-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {templates.map(t => (
                  <tr key={t.template_id} className="border-b border-soft/50 hover:bg-bg-secondary/30" data-testid={`tpl-row-${t.template_id}`}>
                    <td className="py-3 px-2 font-medium">{t.name}</td>
                    <td className="py-3 px-2">{t.sessions_total}</td>
                    <td className="py-3 px-2">{t.price} {t.currency}</td>
                    <td className="py-3 px-2">{t.duration_months ? `${t.duration_months} month${t.duration_months > 1 ? 's' : ''}` : '—'}</td>
                    <td className="py-3 px-2 capitalize text-xs text-fg-muted">{(t.discipline || '').replace('_', ' ')}</td>
                    <td className="py-3 px-2 text-right space-x-2">
                      <button onClick={() => { setEditTemplate(t); setShowTemplate(true); }} className="text-xs text-fg-muted hover:text-brand" data-testid={`tpl-edit-${t.template_id}`}>
                        <Edit2 className="w-3 h-3"/>
                      </button>
                      <button onClick={() => delTemplate(t.template_id)} className="text-xs text-[#B83A3A] hover:opacity-70" data-testid={`tpl-delete-${t.template_id}`}>
                        <Trash2 className="w-3 h-3"/>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {showSell && (
        <SellModal onClose={() => setShowSell(false)} patients={patients} templates={templates}
                   onTemplateCreated={loadTemplates}
                   onSaved={() => { setShowSell(false); load(); }}/>
      )}
      {editPkg && (
        <SellModal initial={editPkg} onClose={() => setEditPkg(null)} patients={patients} templates={templates}
                   onTemplateCreated={loadTemplates}
                   onSaved={() => { setEditPkg(null); load(); }}/>
      )}
      {showTemplate && (
        <TemplateModal initial={editTemplate} onClose={() => setShowTemplate(false)}
                       onSaved={() => { setShowTemplate(false); setEditTemplate(null); loadTemplates(); }}/>
      )}
    </div>
  );
}

/* ---------- Template builder modal (manual price + duration) ---------- */

function TemplateModal({ initial, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: initial?.name || '',
    sessions_total: initial?.sessions_total || 6,
    price: initial?.price ?? '',
    currency: initial?.currency || 'PLN',
    duration_months: initial?.duration_months || '',
    discipline: initial?.discipline || 'physiotherapy',
    notes: initial?.notes || '',
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.name.trim()) { toast.error('Enter a package name.'); return; }
    if (!form.price && form.price !== 0) { toast.error('Enter the price.'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        sessions_total: parseInt(form.sessions_total, 10),
        price: parseFloat(form.price),
        duration_months: form.duration_months ? parseInt(form.duration_months, 10) : null,
      };
      if (initial?.template_id) {
        await api.put(`/package-templates/${initial.template_id}`, payload);
        toast.success('Template updated.');
      } else {
        await api.post('/package-templates', payload);
        toast.success('Package created — it is now available in "Sell a package".');
      }
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed.');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/40 flex items-start justify-center pt-16 px-4" onClick={onClose} data-testid="tpl-modal">
      <div className="bg-bg-primary border border-soft rounded-xl w-full max-w-lg shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b border-soft flex items-center gap-2">
          <LayoutTemplate className="w-4 h-4 text-brand"/>
          <h3 className="font-heading font-semibold">{initial ? 'Edit' : 'Create'} treatment package</h3>
        </div>
        <div className="p-4 space-y-3">
          <Field label="Package name">
            <input className="input" value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
                   placeholder="e.g. 6-Session Back Care Program" data-testid="tpl-modal-name"/>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Sessions total">
              <input type="number" min="1" className="input" value={form.sessions_total}
                     onChange={(e) => setForm(f => ({ ...f, sessions_total: e.target.value }))} data-testid="tpl-modal-sessions"/>
            </Field>
            <Field label="Price (enter manually)">
              <input type="number" min="0" step="0.01" className="input" value={form.price}
                     onChange={(e) => setForm(f => ({ ...f, price: e.target.value }))} data-testid="tpl-modal-price"/>
            </Field>
            <Field label="Currency">
              <select className="input" value={form.currency} onChange={(e) => setForm(f => ({ ...f, currency: e.target.value }))}>
                <option>PLN</option><option>EUR</option><option>USD</option><option>GBP</option><option>CAD</option>
              </select>
            </Field>
            <Field label="Duration">
              <select className="input" value={form.duration_months}
                      onChange={(e) => setForm(f => ({ ...f, duration_months: e.target.value }))} data-testid="tpl-modal-duration">
                <option value="">— No time limit —</option>
                {DURATIONS.map(d => <option key={d} value={d}>{d} month{d > 1 ? 's' : ''}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Discipline">
            <select className="input" value={form.discipline} onChange={(e) => setForm(f => ({ ...f, discipline: e.target.value }))}>
              {DISCIPLINES.map(d => <option key={d} value={d} className="capitalize">{d.replace('_', ' ')}</option>)}
            </select>
          </Field>
          <Field label="Notes (optional)">
            <textarea className="input min-h-[50px] !text-sm" value={form.notes || ''}
                      onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))}/>
          </Field>
        </div>
        <div className="p-4 border-t border-soft flex justify-end gap-2">
          <button onClick={onClose} className="btn-secondary text-sm">Cancel</button>
          <button onClick={save} disabled={saving} className="btn-primary text-sm inline-flex items-center gap-2" data-testid="tpl-modal-save">
            {saving && <Loader2 className="w-3 h-3 animate-spin"/>}
            {saving ? 'Saving…' : 'Save package'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------- Sell / edit sold-package modal ---------- */

function addMonths(months) {
  const d = new Date();
  d.setMonth(d.getMonth() + months);
  return d.toISOString().slice(0, 10);
}

function SellModal({ initial, onClose, onSaved, patients, templates, onTemplateCreated }) {
  const [tplList, setTplList] = useState(templates || []);
  const [showInlineTemplate, setShowInlineTemplate] = useState(false);
  const [form, setForm] = useState({
    patient_id: initial?.patient_id || '',
    name: initial?.name || '',
    discipline: initial?.discipline || 'physiotherapy',
    sessions_total: initial?.sessions_total || 6,
    sessions_used: initial?.sessions_used || 0,
    price: initial?.price || 0,
    currency: initial?.currency || 'PLN',
    expires_on: initial?.expires_on || '',
    notes: initial?.notes || '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => { setTplList(templates || []); }, [templates]);

  const pickTemplate = (name) => {
    const t = tplList.find(x => x.name === name);
    setForm(f => ({
      ...f, name,
      sessions_total: t ? t.sessions_total : f.sessions_total,
      price: t ? t.price : f.price,
      currency: t ? (t.currency || f.currency) : f.currency,
      discipline: t ? (t.discipline || f.discipline) : f.discipline,
      expires_on: t?.duration_months ? addMonths(t.duration_months) : f.expires_on,
    }));
  };

  const save = async () => {
    setSaving(true);
    try {
      if (initial) {
        await api.put(`/treatment-packages/${initial.package_id}`, form);
        toast.success('Saved.');
      } else {
        if (!form.patient_id) { toast.error('Pick a patient.'); return; }
        if (!form.name) { toast.error('Choose a package.'); return; }
        await api.post('/treatment-packages', form);
        toast.success('Package sold.');
      }
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed.');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/40 flex items-start justify-center pt-16 px-4" onClick={onClose} data-testid="tp-modal">
      <div className="bg-bg-primary border border-soft rounded-xl w-full max-w-lg shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b border-soft flex items-center gap-2">
          <Package className="w-4 h-4 text-brand"/>
          <h3 className="font-heading font-semibold flex-1">{initial ? 'Edit' : 'Sell a'} treatment package</h3>
          {!initial && (
            <button onClick={() => setShowInlineTemplate(true)} className="text-xs text-brand inline-flex items-center gap-1 hover:opacity-70" data-testid="tp-modal-create-template">
              <Plus className="w-3 h-3"/> Create a package
            </button>
          )}
        </div>
        <div className="p-4 space-y-3">
          {!initial && (
            <Field label="Patient">
              <select className="input" value={form.patient_id}
                      onChange={(e) => setForm(f => ({ ...f, patient_id: e.target.value }))}
                      data-testid="tp-modal-patient">
                <option value="">— Select —</option>
                {patients.map(p => <option key={p.user_id} value={p.user_id}>{p.full_name || p.name || p.email}</option>)}
              </select>
            </Field>
          )}
          <Field label="Package">
            <select className="input" value={form.name} disabled={!!initial}
                    onChange={(e) => pickTemplate(e.target.value)}
                    data-testid="tp-modal-name">
              <option value="">— Choose a package —</option>
              {tplList.map(t => (
                <option key={t.template_id} value={t.name}>
                  {t.name} · {t.sessions_total} sessions · {t.price} {t.currency}{t.duration_months ? ` · ${t.duration_months} mo` : ''}
                </option>
              ))}
              {initial && !tplList.some(t => t.name === form.name) && form.name && (
                <option value={form.name}>{form.name}</option>
              )}
            </select>
            {tplList.length === 0 && !initial && (
              <p className="text-[11px] text-fg-muted mt-1">No packages defined yet — use "Create a package" above first.</p>
            )}
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Discipline">
              <select className="input" value={form.discipline}
                      onChange={(e) => setForm(f => ({ ...f, discipline: e.target.value }))}
                      data-testid="tp-modal-discipline">
                {DISCIPLINES.map(d => <option key={d} value={d} className="capitalize">{d.replace('_', ' ')}</option>)}
              </select>
            </Field>
            <Field label="Sessions total (set by the package)">
              <input type="number" className="input opacity-70 cursor-not-allowed" value={form.sessions_total}
                     disabled readOnly title="Fixed by the selected package"
                     data-testid="tp-modal-sessions"/>
            </Field>
            <Field label="Price (set by the package)">
              <input type="number" className="input opacity-70 cursor-not-allowed" value={form.price}
                     disabled readOnly title="Fixed by the selected package"
                     data-testid="tp-modal-price"/>
            </Field>
            <Field label="Currency">
              <select className="input" value={form.currency}
                      onChange={(e) => setForm(f => ({ ...f, currency: e.target.value }))}>
                <option>PLN</option><option>EUR</option><option>USD</option><option>GBP</option><option>CAD</option>
              </select>
            </Field>
          </div>
          <Field label="Expires on (auto-set from the package duration)">
            <DateInput value={form.expires_on || ''}
                   onChange={(e) => setForm(f => ({ ...f, expires_on: e.target.value }))} data-testid="tp-modal-expires"/>
          </Field>
          <Field label="Notes">
            <textarea className="input min-h-[60px] !text-sm" value={form.notes || ''}
                      onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))}/>
          </Field>
        </div>
        <div className="p-4 border-t border-soft flex justify-end gap-2">
          <button onClick={onClose} className="btn-secondary text-sm">Cancel</button>
          <button onClick={save} disabled={saving} className="btn-primary text-sm inline-flex items-center gap-2" data-testid="tp-modal-save">
            {saving && <Loader2 className="w-3 h-3 animate-spin"/>}
            {saving ? 'Saving…' : initial ? 'Save' : 'Sell package'}
          </button>
        </div>
      </div>
      {showInlineTemplate && (
        <TemplateModal onClose={() => setShowInlineTemplate(false)}
                       onSaved={async () => {
                         setShowInlineTemplate(false);
                         const r = await api.get('/package-templates');
                         setTplList(r.data || []);
                         onTemplateCreated?.();
                       }}/>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-xs text-fg-muted mb-1">{label}</div>
      {children}
    </label>
  );
}
